import { Database, Eye, HelpCircle, MousePointerClick, Webhook } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function NewsletterTrackingHelp() {
  return (
    <details
      className="group mx-5 sm:mx-6 mt-4 mb-4 rounded-xl border border-sky-200 dark:border-sky-800/60 bg-sky-50/70 dark:bg-sky-950/25 overflow-hidden"
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-medium text-sky-900 dark:text-sky-100 hover:bg-sky-100/60 dark:hover:bg-sky-900/20 [&::-webkit-details-marker]:hidden">
        <HelpCircle className="w-4 h-4 shrink-0" />
        How Postmark tracking works
        <span className="ml-auto text-xs font-normal text-sky-700/80 dark:text-sky-300/80 group-open:hidden">
          Show details
        </span>
      </summary>
      <div className="px-4 pb-4 pt-0 space-y-4 text-sm text-sky-950/90 dark:text-sky-100/90 border-t border-sky-200/80 dark:border-sky-800/50">
        <p className="pt-3 text-sky-900/90 dark:text-sky-100/90">
          We do not track opens or clicks in the browser. When a send reaches{' '}
          <span className="font-medium">sent</span>, Postmark delivers it with open tracking and
          rewritten links. Each time a recipient opens the message or clicks a tracked link, Postmark
          POSTs a webhook to our edge function.
        </p>

        <ul className="space-y-3">
          <li className="flex gap-3">
            <Webhook className="w-4 h-4 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />
            <div>
              <p className="font-medium text-sky-950 dark:text-sky-50">Postmark → webhook</p>
              <p className="text-sky-900/80 dark:text-sky-200/80 mt-0.5">
                Open and Click payloads include <code className="text-xs font-mono">MessageID</code>{' '}
                (matched to <code className="text-xs font-mono">scheduled_emails.postmark_message_id</code>
                ) and                 <code className="text-xs font-mono">ReceivedAt</code> (shown as{' '}
                <span className="font-medium">When</span> in the table — not the original send time). Clicks
                also include <code className="text-xs font-mono">OriginalLink</code> as the{' '}
                <span className="font-medium">Link</span> column in the table above.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <Database className="w-4 h-4 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />
            <div>
              <p className="font-medium text-sky-950 dark:text-sky-50">
                Stored in <code className="text-xs font-mono">email_engagement_events</code>
              </p>
              <p className="text-sky-900/80 dark:text-sky-200/80 mt-0.5">
                One row per webhook — we do not deduplicate. Columns:{' '}
                <code className="text-xs font-mono">event_type</code> (open | click),{' '}
                <code className="text-xs font-mono">occurred_at</code>,{' '}
                <code className="text-xs font-mono">destination_url</code> (clicks only),{' '}
                <code className="text-xs font-mono">scheduled_email_id</code> (links to the recipient
                send row).
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <Eye className="w-4 h-4 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />
            <div>
              <p className="font-medium text-sky-950 dark:text-sky-50">Duplicate opens</p>
              <p className="text-sky-900/80 dark:text-sky-200/80 mt-0.5">
                Postmark may record multiple opens for the same person (re-opens, Apple Mail Privacy
                Protection, preview panes). Each open webhook becomes its own row here. Summary stats
                count <span className="font-medium">unique recipients who opened</span>, not total
                open events.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <MousePointerClick className="w-4 h-4 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />
            <div>
              <p className="font-medium text-sky-950 dark:text-sky-50">Duplicate link clicks</p>
              <p className="text-sky-900/80 dark:text-sky-200/80 mt-0.5">
                Every click is stored separately — the same link clicked twice appears as two rows with
                the same <span className="font-medium">Link</span> URL. Summary stats show unique
                clickers vs total click count.
              </p>
            </div>
          </li>
        </ul>

        <p className="text-xs text-sky-800/75 dark:text-sky-300/75">
          After the measurement window (~14 days), a daily job rolls raw events into{' '}
          <code className="font-mono">email_engagement_rollups</code> and removes old rows from{' '}
          <code className="font-mono">email_engagement_events</code>.{' '}
          <Link
            to="/guide/email-tracking"
            className="font-medium text-sky-700 dark:text-sky-300 underline underline-offset-2 hover:text-sky-900 dark:hover:text-sky-100"
          >
            Full tracking guide →
          </Link>
        </p>
      </div>
    </details>
  );
}
